import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitEditBasic } from "@shared/models/analytics/unit"
import type { CourseColor } from "@shared/models/colors"
import Patch from "@shared/models/patch-updater"
import { get } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  courseId: string
  cardStackId: string
  /** null clears the unit's color. */
  color: CourseColor | null
  previousColor: CourseColor | null
}

export const CHANGE_UNIT_COLOR = {
  name: "CHANGE_UNIT_COLOR",

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId).set(
      "attributes.color",
      payload.color
    ).patch

    let summaryPatch = Patch.create("card-stack-summary", payload.cardStackId).set(
      "attributes.color",
      payload.color
    ).patch

    return [patch, summaryPatch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId).set(
      "attributes.color",
      payload.previousColor
    ).patch

    let summaryPatch = Patch.create("card-stack-summary", payload.cardStackId).set(
      "attributes.color",
      payload.previousColor
    ).patch

    return [patch, summaryPatch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let course = findFn("course", payload.courseId)
    let models = {
      course: course,
      planbook: findFn("planbook", get(course, "relationships.planbook.data.id") as string),
      unit:
        findFn("card-stack", payload.cardStackId) ||
        findFn("card-stack-summary", payload.cardStackId),
    }
    let courseTitle = get(models, "course.attributes.title")
    let newEvent: UnitEditBasic = {
      ...createBlankEvent(),
      root_id: models.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),

      primary_object_type: "unit",
      primary_object_id: models.unit.id,
      primary_object_specifier: "color",
      primary_object_id_and_ancestor_ids: [models.planbook.id, models.course.id],

      adverbial_verb: null,

      adverbial_object_type: null,
      adverbial_object_id: null,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: [models.planbook.id, models.course.id],
    }

    return {
      title: "Changed Unit Color",
      titleForAnalytics: "Unit - Changed Color",
      description: `Changed the color of your unit to ${payload.color}`,
      context: {
        courseTitle,
        planbookTitle: get(models, "planbook.attributes.title"),
        unitId: payload.cardStackId,
        courseId: get(models, "course.id"),
        planbookId: get(models, "planbook.id"),
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${course.id}`,
        to: [`course-edits:course-${course.id}`, `planbook-edits:planbook-${models.planbook.id}`],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default CHANGE_UNIT_COLOR
